/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Build the teacher list column for every section.
 * @state-owner navigation store; dataset store
 * @allowed-imports React; workspace and dataset stores; shared components and hooks; module public APIs
 * @forbidden API client; in-product role switching
 * @public-api ListColumn
 * @side-effects navigation and popover opening
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts and
 *   frontend/apps/web/e2e/act-f13.spec.ts
 */

import { Fragment } from 'react';
import {
  ActivityOutline,
  activityOutlineSubtitle,
  useActivityFilter,
} from '../../../modules/activities';
import { MenuRows } from '../../../shared/components/ContextMenu';
import {
  ListClassHeading,
  ListEmpty,
  ListFilterRow,
  ListGroup,
  ListRow,
} from '../../../shared/components/ListColumn';
import { pointerPoint, useOverlay } from '../../../shared/components/OverlayHost';
import { ACTIVITY_STATUS_LABEL } from '../../../shared/lib/demo-data';
import {
  classRuns,
  findActivity,
  findSeat,
  liveRuns,
  runName,
} from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator, useWorkspacePick } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import type { ListContent } from '../ListColumn';

export function TeacherListBody(): ListContent {
  const t = useTranslator();
  const section = useWorkspaceUiStore((state) => state.section);
  const selection = useWorkspaceUiStore((state) => state.selection);
  const filters = useWorkspaceUiStore((state) => state.filters);
  const setFilter = useWorkspaceUiStore((state) => state.setFilter);
  const clearFilter = useWorkspaceUiStore((state) => state.clearFilter);
  const pick = useWorkspacePick();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const seats = useDatasetStore((state) => state.seats);
  const visibleActivities = useActivityFilter();
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const { openPopover } = useOverlay();

  const valuesOf = (key: string) => filters[section]?.[key] ?? [];
  const activity = findActivity(activities, selection);

  if (
    section === 'act' &&
    activity &&
    activity.status !== 'parsing' &&
    activity.status !== 'failed'
  ) {
    return {
      title: activity.name,
      subtitle: activityOutlineSubtitle(activity, t),
      body: <ActivityOutline activity={activity} />,
    };
  }

  if (section === 'act') {
    const current = valuesOf('ci');
    const pickClass = (index: number | null) => {
      if (index == null) clearFilter('act', 'ci');
      else setFilter('act', 'ci', [String(index)]);
      pick(null);
    };
    return {
      title: t('活動'),
      subtitle: `${visibleActivities.length} / ${activities.length} ${t('項')}`,
      body: (
        <>
          {/* Material lives under the class that uses it; adding an activity to a class makes it
              show up here. What has not been filed yet sits in the last row. */}
          <ListGroup id="a-cls" label={t('班級')}>
            {classes.map((item, index) => (
              <ListFilterRow
                count={
                  activities.filter((entry) => entry.usedByClassIndexes.includes(index)).length
                }
                key={item.id}
                label={item.name}
                on={current.includes(String(index))}
                onContextMenu={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  const point = pointerPoint(event);
                  openPopover({
                    x: point.x,
                    y: point.y,
                    className: 'cmenu rmenu',
                    content: (
                      <MenuRows
                        items={[
                          { label: t('只看這個班'), onSelect: () => pickClass(index) },
                          'divider',
                          {
                            label: t('前往班級'),
                            onSelect: () => navigate('room', `c${index}`),
                          },
                        ]}
                      />
                    ),
                  });
                }}
                onSelect={() => pickClass(current.includes(String(index)) ? null : index)}
              />
            ))}
            <ListFilterRow
              count={activities.filter((entry) => entry.usedByClassIndexes.length === 0).length}
              label={t('未加入班級')}
              on={current.includes('-1')}
              onSelect={() => pickClass(current.includes('-1') ? null : -1)}
            />
          </ListGroup>
          <ListGroup id="a-recent" label={t('最近開啟')}>
            {activities.slice(0, 4).map((item) => (
              <ListRow
                icon={t('題字')}
                key={item.id}
                on={selection === item.id}
                onSelect={() => pick(item.id)}
                subtitle={`${item.questionCount} ${t('題')} · ${t(
                  ACTIVITY_STATUS_LABEL[item.status] ?? item.status,
                )}`}
                title={item.name}
              />
            ))}
          </ListGroup>
        </>
      ),
    };
  }

  if (section === 'room') {
    const active = valuesOf('f');
    const meta = (index: number) => {
      const classRunList = classRuns(runs, index);
      const live = classRunList.filter((run) => run.status === 'live').length;
      return {
        live,
        needs: live
          ? (seats[index] ?? []).filter((seat) => seat.status === 'need' || seat.status === 'stuck')
              .length
          : 0,
        pending: classRunList.filter((run) => run.status === 'ended' && !run.sent).length,
      };
    };
    const keep = (index: number) => {
      if (!active.length) return true;
      const values = meta(index);
      return (
        (active.includes('live') && values.live > 0) ||
        (active.includes('need') && values.needs > 0) ||
        (active.includes('rev') && values.pending > 0)
      );
    };
    const visible = classes.map((_, index) => index).filter(keep);
    return {
      title: t('班級'),
      subtitle: `${visible.length} / ${classes.length} ${t('個班級')}`,
      body: (
        <>
          <ListGroup id="c-list" label={t('我帶的班級')}>
            {visible.length ? (
              visible.map((index) => {
                const item = classes[index];
                const values = meta(index);
                return (
                  <ListRow
                    background={values.needs ? 'var(--error-container)' : undefined}
                    flag={values.needs ? `${values.needs} ${t('人需要你')}` : undefined}
                    foreground={values.needs ? 'var(--error)' : undefined}
                    icon={t('班字')}
                    key={item.id}
                    on={selection === `c${index}`}
                    onSelect={() => pick(`c${index}`)}
                    subtitle={
                      values.live
                        ? `${t('上課中')} · ${item.students.length} ${t('人')}`
                        : item.subtitle
                    }
                    time={values.needs ? '' : `${item.students.length} 人`}
                    title={item.name}
                  />
                );
              })
            ) : (
              <ListEmpty
                description={t('調整搜尋或篩選條件，再試一次。')}
                title={t('沒有符合的班級')}
              />
            )}
          </ListGroup>
          <ListGroup id="c-reports" label={t('報告')}>
            <ListRow
              icon={t('報字')}
              onSelect={() => navigate('rep', null)}
              subtitle={t('查看學生學習報告')}
              title={t('報告')}
            />
          </ListGroup>
        </>
      ),
    };
  }

  if (section === 'live') {
    const all = liveRuns(runs);
    const active = valuesOf('f');
    const classFilter = valuesOf('ci');
    const needsOf = (classIndex: number) =>
      (seats[classIndex] ?? []).filter((seat) => seat.status === 'need' || seat.status === 'stuck')
        .length;
    const visible = all.filter(
      (run) =>
        (!active.length ||
          (active.includes('need') && needsOf(run.classIndex) > 0) ||
          (active.includes('ok') && needsOf(run.classIndex) === 0)) &&
        (!classFilter.length || classFilter.includes(String(run.classIndex))),
    );
    return {
      title: t('即時'),
      subtitle: all.length
        ? `${visible.length} / ${all.length} ${t('項進行中')}`
        : t('現在沒有課在上'),
      body: all.length ? (
        <ListGroup id="l-list" label={t('正在進行')}>
          {visible.length ? (
            classes.map((item, classIndex) => {
              const rows = visible.filter((run) => run.classIndex === classIndex);
              if (!rows.length) return null;
              return (
                <Fragment key={item.id}>
                  <ListClassHeading count={rows.length} label={item.name} />
                  {rows.map((run) => {
                    const needs = needsOf(run.classIndex);
                    return (
                      <ListRow
                        background={needs ? 'var(--error-container)' : undefined}
                        flag={needs ? `${needs} ${t('人需要你')}` : undefined}
                        foreground={needs ? 'var(--error)' : undefined}
                        icon={t('活字')}
                        key={run.id}
                        on={selection === run.id}
                        onSelect={() => pick(run.id)}
                        subtitle={`${classes[run.classIndex].students.length} ${t('人')} · ${t(run.dateLabel)}`}
                        time={needs ? '' : t('順利')}
                        title={runName(activities, run)}
                      />
                    );
                  })}
                </Fragment>
              );
            })
          ) : (
            <ListEmpty description={t('調整篩選條件，再試一次。')} title={t('沒有符合的課堂')} />
          )}
        </ListGroup>
      ) : (
        <ListEmpty
          description={t('到班級的活動分頁按「發放」，選課堂進行。')}
          title={t('現在沒有課在上')}
        />
      ),
    };
  }

  const active = valuesOf('f');
  const classFilter = valuesOf('ci');
  const needsAttention = (student: string) =>
    classes.some(
      (item, index) =>
        item.students.includes(student) && findSeat(seats[index] ?? [], student)?.status === 'need',
    );
  const reportGroups = classes.flatMap((item, index) => {
    if (classFilter.length && !classFilter.includes(String(index))) return [];
    const students = item.students.filter((student) => !active.length || needsAttention(student));
    return students.length ? [{ index, item, students }] : [];
  });

  return {
    title: t('報告'),
    subtitle: t('依班級分組'),
    body: (
      <>
        {reportGroups.length ? (
          reportGroups.map(({ index, item, students }) => (
            <ListGroup count={students.length} id={`p-${index}`} key={item.id} label={item.name}>
              {students.map((student) => (
                <ListRow
                  background={needsAttention(student) ? 'var(--error-container)' : undefined}
                  foreground={needsAttention(student) ? 'var(--error)' : undefined}
                  icon={student[0]}
                  key={`${item.id}-${student}`}
                  on={selection === student}
                  onSelect={() => pick(student)}
                  subtitle={needsAttention(student) ? '需要注意' : t('近 30 天')}
                  title={student}
                />
              ))}
            </ListGroup>
          ))
        ) : (
          <ListEmpty
            description={t('學生完成活動後，學習報告會顯示在這裡。')}
            title={t('還沒有學生報告')}
          />
        )}
      </>
    ),
  };
}
